import { CancellationFlowStatus } from '../_enums/cancellation/cancellation-flow-status.enum';
import { CustomerAccountType } from '../_enums/customer-account-type.enum';
import { EntityType } from '../_enums/entity-type.enum';
import { FamilyMemberType } from '../_enums/family-member-type.enum';
import { InsuranceFlowChannel } from '../_enums/insurance-flow/insurance-flow-channel.enum';
import { InsuranceFlowStateHistoryStatus } from '../_enums/insurance-flow/insurance-flow-history-status.enum';
import { InsuranceFlowSource } from '../_enums/insurance-flow/insurance-flow-source.enum';
import { InsuranceFlowStatus } from '../_enums/insurance-flow/insurance-flow-status.enum';
import { InsuranceType } from '../_enums/insurance-type.enum';
import { LeadStatus } from '../_enums/lead-status.enum';
import { OfferType } from '../_enums/offer.enums';
import {
  PolicyOrigin,
  PolicyStatus,
  ProposalState,
} from '../_enums/proposal.enums';
import { SignatureType } from '../_enums/signature-type.enum';
import { InsuranceFlowSortElement } from '../_enums/task-sort.enums';
import { CancellationFlowSummary } from './cancellation.types';
import { Claim } from './claim.types';
import { InsuranceMetadatas, Lead } from './lead.types';
import {
  CategoryDetails,
  PolicyDetails,
  ProductDetails,
  ProposalDetails,
} from './proposals.type';

export type InsuranceFlowEntityType =
  | EntityType.ADMIN_ID
  | EntityType.USER_ID
  | EntityType.MEMBER_ID
  | EntityType.FAMILY_MEMBER_ID
  | EntityType.PARTNER_ID
  | 'AGENT_ID';

export type InsuranceFlowSummary = {
  id: string;
  displayId: string;
  status: InsuranceFlowStatus;
  userType: CustomerAccountType;
  memberType: FamilyMemberType;
  userId: string | null;
  memberId: string | null;
  partnerId: string | null;
  agentId: string | null;
  customerSupportId: string | null;
  customerDisplayId: string | null;
  customerName: string | null;
  partnerName: string | null;
  agentName: string | null;
  customerSupportName: string | null;
  insuranceType: InsuranceType | null;
  source: InsuranceFlowSource | null;
  createdOn: string;
  lastUpdatedOn: string;
  address: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  policyDetails: PolicyDetails | null;
  recommendedRenewalDate: Date | null;
  sourceType: InsuranceType;
  changeReason: string[] | null;
  origin: PolicyOrigin;
  leadMetadata?: InsuranceMetadatas;
  /** Status of the cancellation under way on this flow, if any. */
  runningCancellationStatus?: CancellationFlowStatus | null;
};

/**
 * One entry of the "Affaire concernée" picker.
 *
 * Deliberately lean — the search is scoped to a single family member, so the
 * customer's name would be identical on every row and is not returned.
 */
export type InsuranceFlowOption = {
  id: string;
  displayId: string | null;
  status: InsuranceFlowStatus;
  /** Empty until the flow has a proposal in a policy-bearing state. */
  policyId: string | null;
  /** The day that policy takes effect, empty for the same reason as `policyId`. */
  startDate: string | null;
  /**
   * The insurer's catalogue category id, empty for the same reason as
   * `policyId`. It is what an exchange with that insurer is addressed with, so
   * the company never has to be matched back by name.
   */
  categoryId: string | null;
  /** The insurer/category in French, empty for the same reason as `policyId`. */
  categoryName: string | null;
  /**
   * The product in French — the proposal's sub-category, not its company;
   * `categoryName` is the insurer. Empty for the same reason as `policyId`, and
   * also whenever the representative proposal names no sub-category at all, so
   * an option has to read correctly without it.
   */
  subcategoryName: string | null;
  /** The representative policy proposal behind `policyId`; empty for the same reason. */
  proposalId: string | null;
  insuranceType: InsuranceType | null;
  createdOn: string;
};

/**
 * What an "Affaire" picker is choosing, so the server offers only the statuses
 * that role accepts: a cancellation's replacement, or the policy it cancels.
 */
export type InsuranceFlowSearchPurpose = 'REPLACEMENT' | 'BASE_POLICY';

export type SearchInsuranceFlowsParams = {
  /** Mandatory: this endpoint only answers "which affaires belong to this member". */
  familyMemberId: string;
  query?: string;
  /**
   * Exact match on the affaire's display id, for a picker that must stay within
   * one affaire. Unlike `query`, which is a contains-match and would also
   * return the neighbouring ids.
   */
  displayId?: string;
  statuses?: InsuranceFlowStatus[];
  /** Clamped server-side; omit to take the backend default. */
  limit?: number;
  /** Only flows that carry a proposal, i.e. whose `proposalId` is set. */
  withPolicyOnly?: boolean;
  /** Only flows of the given type. */
  insuranceType?: InsuranceType;
  /** Only flows whose status suits this role; omit for any status. */
  purpose?: InsuranceFlowSearchPurpose;
};

/** No total: it backs a type-ahead, and nobody pages a dropdown. */
export type SearchInsuranceFlowsResponse = {
  items: InsuranceFlowOption[];
};

export type AllInsuranceFlowsResponse = {
  flowsList: InsuranceFlowSummary[];
  /** The cancellations of the page's affaires; only when asked for. */
  cancellations?: CancellationFlowSummary[] | null;
};

export type GetInsuranceFlowsParams = {
  entityType: InsuranceFlowEntityType;
  entityId: string;
  pageSize: number;
  pageCount: number;
  startDate?: string | Date | null;
  endDate?: string | Date | null;
  partnerId?: string;
  agentId?: string;
  userId?: string;
  memberId?: string;
  searchBy?: string | null;
  insuranceTypes?: InsuranceType[];
  excludedInsuranceTypes?: InsuranceType[];
  flowStatus?: InsuranceFlowStatus[] | null;
  sortBy?: InsuranceFlowSortElement | null;
  ascending?: boolean;
  includeRecommendedRenewalDate?: boolean;
  includeLeadSource?: boolean;
  includeLeadMetadata?: boolean;
  includeCancellations?: boolean;
};

export type InsuranceFlowsExportParams = Omit<
  GetInsuranceFlowsParams,
  'pageSize' | 'pageCount'
> & {
  withFullHistory: boolean;
  mode: 'REGULAR' | 'CENTRALISATION';
};

export type InsuranceFlow = {
  id: string;
  displayId: string;
  status: InsuranceFlowStatus;
  insuranceType: InsuranceType;
  source: InsuranceFlowSource;
  createdOn: string;
  lastUpdatedOn: string;
  userId: string;
  memberId: string;
  partnerId: string;
  agentId: string;
  customerSupportName: string;
};

type InsuranceFlowHistoryBaseElement = {
  id: string;
  flowId: string;
  status: InsuranceFlowStatus;
  updatedOn: string;
  updatedBy: string | null;
  endedOn: string;
  endedBy: string | null;
  leadSnapshot: InsuranceFlowLeadSnapshotResponse;
  proposalSnapshot: InsuranceFlowProposalSnapshotResponse;
  channel: InsuranceFlowChannel;
};

export type InsuranceFlowProposalSnapshotResponse = {
  proposalId: string;
  partnerId?: string;
  offerId?: string;
  policyId?: string;
  leadId?: string;
  insuranceType?: InsuranceType;
  offerType?: OfferType;
  policyType?: string;
  startDate?: string;
  endDate?: string;
  keyTags?: string;
  periodicity?: string;
  coversPeriodicity?: string;
  premiumAmount?: number;
  policyDocs?: string;
  policyVisibility?: string;
  memberId?: string;
  familyMemberId?: string;
  categoryDetails?: CategoryDetails;
  productDetails?: ProductDetails;
  agentId?: string;
  totalAmount?: number;
  keyFeatures?: string;
  productBenefits?: string;
  signedDoc?: string;
  contractDoc?: string[];
  proposalState?: ProposalState;
  policyStatus?: PolicyStatus;
  isDeleted?: boolean;
  customerSupportId?: string;
  centralisationId?: string;
  completionTime?: string;
  managedByLty?: boolean;
  flowId?: string;
  signedOfferId?: number;
  offerSignatureDate?: string;
  signatureType?: SignatureType;
  createdBy?: string;
  updatedBy?: string;
  createdOn?: string;
  lastUpdatedOn?: string;
};

export type InsuranceFlowLeadSnapshotResponse = {
  leadId: string;
  insuranceType?: InsuranceType;
  partnerId?: string;
  agentId?: string;
  userId?: string;
  memberId?: string;
  metadata?: InsuranceMetadatas;
  mainLeadId?: string;
  status?: LeadStatus;
  isDeleted?: boolean;
  originalProposalId?: string;
  originalLeadId?: string;
  customerSupportId?: string;
  changeReason?: string[];
  completionTime?: string;
  flowId?: string;
  createdBy?: string;
  updatedBy?: string;
  createdOn?: string;
  lastUpdatedOn?: string;
};

export type InsuranceFlowStateHistory = Omit<
  InsuranceFlowHistoryBaseElement,
  'status'
> & {
  status: InsuranceFlowStateHistoryStatus;
  updatedByFullName: string | null;
  endedByFullName: string | null;
};

export type InsuranceFlowRelations = {
  statusHistory: InsuranceFlowStateHistory[];
  lead: Lead | null;
  proposals: ProposalDetails[];
  policy: ProposalDetails | null;
};

export type InsuranceFlowDetailsResponse = InsuranceFlow &
  InsuranceFlowRelations & {
    userName: string;
    memberName: string;
    partnerName: string;
    agentName: string;
    customerSupportName: string;
  };

export type InsuranceFlowTasksResponse = {
  flowsList: InsuranceFlowSummary[];
  claimsList: Claim[];
  /** The cancellations of the page's affaires; only when asked for. */
  cancellations?: CancellationFlowSummary[] | null;
};

export type InsuranceFlowFullHistoryElement = InsuranceFlowStateHistory & {
  displayId: string;
  customerDisplayId: string;
  policyNumber: string;
  source: InsuranceFlowSource;
  signatureType: SignatureType;
};

export type InsuranceFlowFullHistoryResponse = {
  history: InsuranceFlowFullHistoryElement[];
};

export type CentralisationFlowRequest = {
  flowId: string;
};

export type SendCentralisationFlowRequest = {
  flowId: string;
  skipEmail: boolean;
  lsa45DocumentId?: number | null;
  /** Required unless `skipEmail`; the other documents are optional. */
  managementAgreementDocumentId?: number | null;
  identityCardDocumentId?: number | null;
  signatureCertificateDocumentId?: number | null;
  /**
   * One edited body per mail, keyed by the render's recipient. Required unless
   * `skipEmail`; the backend rejects keys the send does not re-resolve.
   */
  bodies?: CentralisationMailBody[];
  /**
   * Documents of the flow's customer, picked by the operator, added to every
   * mail. Ignored when no mail goes out.
   */
  attachmentDocumentIds?: number[];
};

/** One mail's HTML prose (letterhead excluded), keyed by the recipient the render named. */
export type CentralisationMailBody = {
  companyId: string;
  insuranceType: InsuranceType;
  body: string;
};

export type CentralisationFlowResponse = {
  status: 'SUCCESS';
};

export type SendInsuranceFlowContractRequest = {
  proposalId: string;
  channel: InsuranceFlowChannel;
  managementAgreementDocumentId?: number | null;
  identityCardDocumentId?: number | null;
  signatureCertificateDocumentId?: number | null;
  /** HTML prose of the mail, letterhead excluded. Required iff `channel` is IN_APP. */
  body?: string;
  /** See `SendCentralisationFlowRequest.attachmentDocumentIds`. */
  attachmentDocumentIds?: number[];
};

export type SendInsuranceFlowContractResponse = {
  status: 'SUCCESS';
};

/** Display only: the send re-resolves its recipients itself, so this cannot redirect a mail. */
export type CompanyMailRecipient = {
  companyId: string;
  insuranceType: InsuranceType;
  email: string;
};

/**
 * The prefill of the signed-contract mail: the subject, the template's prose
 * the send request expects back as `body` once edited, and the recipient the
 * send will resolve.
 */
export type RenderCompanyMailResponse = {
  subject: string;
  body: string;
  recipient: CompanyMailRecipient;
};

/**
 * The prefills of the centralisation mandate mails, one per mail the send will
 * dispatch: a centralisation fans out per selected company and branch. Each
 * recipient's (companyId, insuranceType) keys the edited body on the send.
 */
export type RenderCentralisationMailsResponse = {
  mails: {
    recipient: CompanyMailRecipient;
    subject: string;
    body: string;
  }[];
};

export type CancelInsuranceFlowContractRequest = {
  flowId: string;
  byInsurer: boolean;
};

export type CancelInsuranceFlowContractResponse = {
  status: 'SUCCESS';
};

/** First line of an "Affaire" option: the flow's public id. */
export const getInsuranceFlowOptionTitle = (
  option: InsuranceFlowOption
): string => option.displayId ?? option.id;

/**
 * Second line of an "Affaire" option: insurer, product and policy number. Split
 * off the id so a narrow dropdown does not wrap it into an unreadable run, and
 * because the insurer and the product are what separate two flows sharing a
 * policy number — or, more often, two flows with the same insurer.
 *
 * A flow whose proposal names no product is simply shorter: every part is
 * filtered out when blank, so nothing renders a dash or an empty slot.
 */
export const getInsuranceFlowOptionDetail = (
  option: InsuranceFlowOption
): string =>
  [option.categoryName, option.subcategoryName, option.policyId]
    .filter((part): part is string => !!part)
    .join(' · ');

/**
 * `LTY-2026-0043 · Assura · LAMal complémentaire · 123.456.789` — the
 * single-line form.
 */
export const getInsuranceFlowOptionLabel = (
  option: InsuranceFlowOption
): string =>
  [
    option.displayId,
    option.categoryName,
    option.subcategoryName,
    option.policyId,
  ]
    .filter((part): part is string => !!part)
    .join(' · ') || option.id;
